import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Mail, Eye, EyeOff, ArrowRight, ShieldCheck, Building2 } from "lucide-react";
import { formatApiError, setTenantSlug } from "../../lib/api";
import { BRAND } from "./landingData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHITE = { background: "#fff" };
const inputCls = "w-full h-12 rounded-xl border-2 border-slate-200 pl-10 pr-10 text-base text-slate-900 focus:outline-none focus:border-blue-600 transition-colors";

export function LandingLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [choices, setChoices] = useState(null);

  const enter = (data) => {
    const { token, tenant } = data;
    if (tenant.domain && tenant.domain !== window.location.hostname) {
      window.location.href = `https://${tenant.domain}/#sso=${encodeURIComponent(token)}`;
      return;
    }
    localStorage.setItem("kl_token", token);
    setTenantSlug(tenant.slug);
    toast.success(`Benvenuto in ${tenant.name}`);
    window.location.href = `${tenant.path_prefix || ""}/`;
  };

  const submit = async (e, slug) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/platform/public/login`, { email, password, slug: slug || null });
      if (data.choose) { setChoices(data.choose); return; }
      enter(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="login" className="bg-[#F3F6FB] scroll-mt-20" data-testid="landing-login">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6 space-y-4">
          <span className="inline-block text-[11px] font-bold uppercase tracking-widest text-blue-700 bg-blue-50 ring-1 ring-blue-200 rounded-full px-3 py-1">Spazio login</span>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900" data-testid="login-title">Sei già un operatore? Entra nella tua console</h2>
          <p className="text-base md:text-lg text-slate-600 max-w-xl">Inserisci le credenziali del tuo staff: riconosciamo automaticamente il circuito a cui appartieni e ti portiamo nel pannello giusto.</p>
          <ul className="text-sm text-slate-600 space-y-2 pt-2">
            <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Connessione cifrata, sessione di 12 ore</li>
            <li className="flex items-center gap-2"><Building2 className="h-4 w-4 text-blue-700" /> Un solo accesso per tutti i circuiti di cui fai parte</li>
          </ul>
        </div>
        <div className="lg:col-span-6">
          <form onSubmit={(e) => submit(e)} className="max-w-md mx-auto lg:ml-auto rounded-3xl bg-white ring-1 ring-slate-200 shadow-xl p-7 sm:p-8 space-y-4" data-testid="landing-login-form">
            <div className="font-heading font-bold text-lg text-slate-900">Pannello operatori & admin</div>
            {choices ? (
              <div className="space-y-2" data-testid="login-choose-tenant">
                <p className="text-sm text-slate-600">La tua email è attiva su più circuiti: scegli dove entrare.</p>
                {choices.map((c) => (
                  <button key={c.slug} type="button" disabled={busy} onClick={(e) => submit(e, c.slug)} className="w-full flex items-center justify-between rounded-xl ring-1 ring-slate-200 px-4 h-12 text-sm font-semibold text-slate-800 hover:bg-slate-50" data-testid={`login-choose-${c.slug}`}>{c.name} <ArrowRight className="h-4 w-4" /></button>
                ))}
                <button type="button" onClick={() => setChoices(null)} className="text-xs text-slate-500 hover:text-slate-800" data-testid="login-choose-back">← Torna indietro</button>
              </div>
            ) : (
              <>
                <label className="block space-y-1.5">
                  <span className="text-sm font-semibold text-slate-700">Email</span>
                  <span className="relative block"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@azienda.it" style={WHITE} className={inputCls} data-testid="landing-login-email" /></span>
                </label>
                <label className="block space-y-1.5">
                  <span className="text-sm font-semibold text-slate-700">Password</span>
                  <span className="relative block"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input type={show ? "text" : "password"} required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" style={WHITE} className={inputCls} data-testid="landing-login-password" />
                    <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" aria-label="Mostra password" data-testid="landing-login-toggle-password">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button></span>
                </label>
                <button type="submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 rounded-full h-12 text-sm font-semibold text-white shadow-md hover:shadow-lg disabled:opacity-60 transition-[box-shadow,transform] hover:-translate-y-0.5" style={{ background: BRAND }} data-testid="landing-login-submit">{busy ? "Accesso in corso…" : <>Accedi alla console <ArrowRight className="h-4 w-4" /></>}</button>
              </>
            )}
            <div className="flex items-center justify-between text-xs text-slate-500 pt-1">
              <a href="/cliente/accedi" className="hover:text-slate-800" data-testid="landing-client-login-link">Sei un cliente? Area clienti</a>
              <a href="/platform" className="hover:text-slate-800 font-semibold" data-testid="superadmin-login-link">Pannello piattaforma</a>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
